/*
 * (c) Copyright, 2025, Medidata Solutions Worldwide, Inc. All rights reserved.
 */

import React, { useEffect, useState } from 'react'
import { Button, FormGroup, FormLabel } from 'react-bootstrap'
import { Formik, FieldArray, yupToFormErrors } from 'formik'
import Modal from '~/components/Modal'
import Loader from '~/components/shared/Loader'
import { Select } from '~/components/Select'
import { ErrorMessageStyled } from '~/components/shared/ErrorMessage'
import { UserGroupSteps } from '~/components/workflow/UserGroupSteps'
import { AddCustomJustificationModal } from '~/components/modals/AddCustomJustificationModal'
import Utils from '~/lib/Utils'
import { useServiceContext } from '~/root'
import { useTranslation } from 'react-i18next'
import { useModal } from '~/contexts/ModalContext'
import { toast } from '~/services/toast'
import { FsiType } from '~/constants/fsi'
import { WorkflowType, WorkflowTypeType } from '~/constants/workflow'
import {
  getDetails,
  getFileCRApprovers,
  getFileApprovers,
  assembleFsiCRApproversForServer,
  assembleFsiApproversForServer
} from '~/services/api/files'
import { documentTypesApiService } from '~/services/api/api.documenttypes'
import { getJustifications } from '~/services/api/api.settings'
import { requestDocumentChange } from '~/services/api/workflows'
import createValidationSchema from './FormValidation'
import WorkflowAuthorizationInfoModal from './WorkflowAuthorizationInfoModal'

interface RemoveDocumentModalProps {
  fsiId: number
  params?: {
    selectedIds?: number[]
    [key: string]: any
  }
  onClose?: () => void
}

interface FileType {
  id?: number
  name?: string
  type?: string
  fsiTypeTemplate?: { id: number }
  [key: string]: any
}

interface DocType {
  requireJustification?: boolean
  worfklowTemplate?: { id: number }
  [key: string]: any
}

interface JustificationType {
  id: number
  text: string
}

interface OptionType {
  value: number
  label: string
  [key: string]: any
}

interface FormValues {
  crApprovers: any
  approvers: any
  description: string
  docType: DocType | null
  justificationTextId?: number
  justificationText?: string
}

const RemoveDocumentModal: React.FC<RemoveDocumentModalProps> = ({ fsiId, params, onClose }) => {
  const validationSchema = createValidationSchema()
  const context = useServiceContext()
  const { t: translate } = useTranslation()
  const { showModal, hideModal } = useModal()
  const [isOpen, setIsOpen] = useState<boolean>(false)
  const [loading, setLoading] = useState<boolean>(false)
  const [file, setFile] = useState<FileType>({})
  const [docType, setDocType] = useState<DocType>({})
  const [availableJustifications, setAvailableJustifications] = useState<OptionType[]>([])
  const [modalTitle, setModalTitle] = useState<string>('')
  const [description, setDescription] = useState<string>('')
  const [showAddCustomModal, setShowAddCustomModal] = useState<boolean>(false)
  const [initValues, setInitValues] = useState<FormValues>({
    crApprovers: null,
    approvers: null,
    description: '',
    docType: null
  })

  useEffect(() => {
    setIsOpen(true)
  }, [])

  useEffect(() => {
    // Load justifications
    getJustifications('').then(function (data: unknown) {
      const justData = data as { elements: JustificationType[] }
      const avlJust = justData.elements ?? []
      avlJust.push({ id: 0, text: translate('CUSTOM...') })
      setAvailableJustifications(Utils.transformToSelectOptions(avlJust, 'id', 'text') as OptionType[])
    })

    // Load file details
    getDetails(fsiId).then(function (data: unknown) {
      const fileData = data as FileType
      setFile(fileData)

      // Set modal title and description based on file type
      switch (fileData.type) {
        case FsiType.REFERENCE:
          setModalTitle(translate('BROWSER_ACTION_REQUEST_DOCUMENT_DELETE_REFERENCE'))
          setDescription(translate('CR_DESC_DEFAULT_PLACEHOLDER_REMOVE_REFERENCE'))
          setInitValues((prev) => ({
            ...prev,
            description: translate('CR_DESC_DEFAULT_PLACEHOLDER_REMOVE_REFERENCE')
          }))
          break
        case FsiType.PLACEHOLDER:
          setModalTitle(translate('BROWSER_ACTION_REQUEST_DOCUMENT_DELETE_PLACEHOLDER'))
          setDescription(translate('CR_DESC_DEFAULT_PLACEHOLDER_REMOVE_PLACEHOLDER'))
          setInitValues((prev) => ({
            ...prev,
            description: translate('CR_DESC_DEFAULT_PLACEHOLDER_REMOVE_PLACEHOLDER')
          }))
          break
        case FsiType.DOCUMENT:
        default:
          setModalTitle(translate('BROWSER_ACTION_REQUEST_DOCUMENT_DELETE_DOCUMENT'))
          setDescription(translate('CR_DESC_DEFAULT_PLACEHOLDER_REMOVE'))
          setInitValues((prev) => ({
            ...prev,
            description: translate('CR_DESC_DEFAULT_PLACEHOLDER_REMOVE')
          }))
          break
      }

      // Load document type details
      if (fileData.fsiTypeTemplate?.id) {
        documentTypesApiService.getById(fileData.fsiTypeTemplate.id).then(function (docTypeData: unknown) {
          const dt = docTypeData as DocType
          setDocType(dt)

          if (dt?.requireJustification) {
            setInitValues((prev) => ({
              ...prev,
              justificationTextId: -1,
              justificationText: ''
            }))
          }

          setInitValues((prev) => ({
            ...prev,
            docType: dt
          }))
        })
      }
    })

    // Load CR approvers and approvers
    Promise.all([getFileCRApprovers(fsiId, true), getFileApprovers(fsiId)]).then(function (results) {
      const crApproversData = results[0]
      const approversData = results[1]

      setInitValues((prev) => ({
        ...prev,
        crApprovers: crApproversData,
        approvers: approversData
      }))
    })
  }, [fsiId, translate])

  function save(values: FormValues) {
    // Determine workflow type based on file type
    let workflowTypeTmp: WorkflowTypeType = WorkflowType.OBSOLETE
    if (file.type === FsiType.REFERENCE) {
      workflowTypeTmp = WorkflowType.OBSOLETE_REFERENCE
    } else if (file.type === FsiType.PLACEHOLDER) {
      workflowTypeTmp = WorkflowType.OBSOLETE_PLACEHOLDER
    }

    // Build request object
    const requestToSend: any = {
      description: values.description,
      initialApprovers: null,
      approvers: null,
      parentFolderId: 0,
      fileIds: [fsiId],
      workflowType: workflowTypeTmp
    }

    // Handle justification
    if (docType?.requireJustification) {
      if (values.justificationTextId === 0) {
        requestToSend.justificationTextId = null
        requestToSend.customJustificationText = values.justificationText
      } else {
        requestToSend.justificationTextId = values.justificationTextId
        requestToSend.customJustificationText = null
      }
    }

    // Assemble CR approvers
    let initialApprovers: any[] = []
    if (values.crApprovers?.initialApprovers?.type === 'SETUP_WITH_WORKFLOW') {
      requestToSend.initialApprovers = assembleFsiCRApproversForServer(values.crApprovers).initialApprovers.steps
      initialApprovers = requestToSend.initialApprovers
    } else if (values.crApprovers?.initialApprovers?.type === 'USE_PREDEFINED') {
      initialApprovers = assembleFsiCRApproversForServer(values.crApprovers).initialApprovers.steps
    }

    // Assemble approvers
    if (values.approvers?.approvers?.type === 'SETUP_WITH_WORKFLOW') {
      requestToSend.approvers = assembleFsiApproversForServer(values.approvers).approvers.steps
    }

    // Make API call
    const apiCall = requestDocumentChange(requestToSend)
    apiCall.then(
      function (addCrResponse: any) {
        doClose()

        // Check if current user is the only approver
        const currentUserId = context.service.auth.getUserId()
        const iAmOnlyApprover =
          Array.isArray(initialApprovers) &&
          initialApprovers.length === 1 &&
          initialApprovers[0].taskTeams?.length === 1 &&
          initialApprovers[0].taskTeams[0].user &&
          initialApprovers[0].taskTeams[0].user.id === currentUserId

        if (iAmOnlyApprover) {
          // Show authorization info modal
          setTimeout(function () {
            showModal(
              <WorkflowAuthorizationInfoModal
                taskStatus="OPEN"
                workflowId={addCrResponse.id}
                onClose={() => hideModal()}
              />,
              { keepParentModalsOpen: true }
            )
          })
        } else {
          if (initialApprovers.length > 0) {
            toast.success(translate('BROWSER_REQUEST_REMOVAL_SUCCESS_MSG'))
          }
          // Trigger updates
          window.location.reload()
        }
      },
      function (data: any) {
        if (data && data.data && 'message' in data.data) {
          toast.error(data.data.message)
        } else {
          toast.error(translate('SOMETHING_WENT_WRONG_MSG_ERROR'))
        }
      }
    )
    return apiCall
  }

  const doClose = () => {
    setIsOpen(false)
    if (onClose) {
      onClose()
    }
  }

  const handleAddCustomJustification = (setFieldValue: (field: string, value: any) => void, currentValue: string) => {
    // Store the current value and show custom justification modal
    setShowAddCustomModal(true)
  }

  return (
    <>
      <Modal title={modalTitle} size="lg" show={isOpen} handleClose={doClose} className="remove-doc theme-sandman">
        <Loader blocking={loading}>
          <Formik<FormValues>
            initialValues={initValues}
            enableReinitialize={true}
            validate={(values: FormValues) => {
              return validationSchema
                .validate(values, {
                  abortEarly: false,
                  context: values
                })
                .then(
                  () => ({}),
                  (err: unknown) => yupToFormErrors(err)
                )
            }}
            onSubmit={(values: FormValues) => {
              save(values)
            }}
          >
            {({ setFieldValue, values, handleSubmit }) => (
              <>
                <Modal.Body>
                  {/* Document Name */}
                  <FormGroup className="form-group md-form-group wparag">
                    <label>{translate('DOCUMENT')}</label>
                    <p>{file.name ?? ''}</p>
                  </FormGroup>

                  {/* Description */}
                  <FormGroup className="wparag w50p mb15" controlId="remove-doc-description">
                    <FormLabel>{translate('DESCRIPTION')}</FormLabel>
                    <textarea
                      className="form-control"
                      maxLength={254}
                      name="description"
                      onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => {
                        setFieldValue('description', e.target.value)
                      }}
                      id="remove-doc-description-input"
                      value={values?.description || ''}
                      rows={6}
                    />
                    <ErrorMessageStyled name="description" />
                  </FormGroup>

                  {/* Justification (if required by document type) */}
                  {docType?.requireJustification && (
                    <FormGroup controlId="remove-doc-justification" className="wparag w50p mb15">
                      <FormLabel>{translate('JUSTIFICATION')}</FormLabel>
                      <Select
                        name="justificationTextId"
                        value={values.justificationTextId}
                        options={availableJustifications}
                        onChange={(option: any) => {
                          setFieldValue('justificationTextId', option ? option.value : null)
                        }}
                        placeholder={translate('SELECT_JUSTIFICATION_LABEL')}
                        searchable
                        isClearable={false}
                      />

                      {/* Custom justification button */}
                      {values.justificationTextId === 0 && (
                        <button
                          data-action="add-description"
                          className="btn btn-chita btn-add-step btn-second mt10"
                          type="button"
                          onClick={() => {
                            // Create a temporary request object for the modal
                            const tempRequest = { customJustificationText: values.justificationText || '' }
                            showModal(
                              <AddCustomJustificationModal
                                request={tempRequest}
                                setRequest={(updater: (prev: any) => any) => {
                                  const updated = updater(tempRequest)
                                  setFieldValue('justificationText', updated.customJustificationText)
                                }}
                                setShowAddCustomModal={() => hideModal()}
                              />,
                              { keepParentModalsOpen: true }
                            )
                          }}
                        >
                          {translate('DESCRIPTION')}
                        </button>
                      )}
                      <ErrorMessageStyled name="justificationTextId" />
                    </FormGroup>
                  )}

                  {/* CR Approvers (if type is SETUP_WITH_WORKFLOW) */}
                  {values?.crApprovers?.initialApprovers?.type === 'SETUP_WITH_WORKFLOW' ? (
                    <FieldArray name="crApprovers.initialApprovers.steps">
                      {(arrayHelpers) => (
                        <UserGroupSteps
                          arrayHelpers={arrayHelpers}
                          values={values?.crApprovers?.initialApprovers?.steps}
                          nofallTxt="BROWSER_FILE_SETTINGS_CR_APPROVERS_INFO_LABEL"
                          labelTxt="BROWSER_FILE_SETTINGS_CR_APPROVERS_APPROVAL_SEQUENCE_HEADLINE"
                          increaseUserGroupWidth={true}
                        />
                      )}
                    </FieldArray>
                  ) : null}

                  {/* Approvers (if type is SETUP_WITH_WORKFLOW) */}
                  {values?.approvers?.approvers?.type === 'SETUP_WITH_WORKFLOW' ? (
                    <FieldArray name="approvers.approvers.steps">
                      {(arrayHelpers) => (
                        <UserGroupSteps
                          arrayHelpers={arrayHelpers}
                          values={values?.approvers?.approvers?.steps}
                          nofallTxt="BROWSER_FILE_SETTINGS_APPROVERS_INFO_LABEL"
                          labelTxt="BROWSER_FILE_SETTINGS_APPROVERS_APPROVAL_SEQUENCE_HEADLINE"
                          increaseUserGroupWidth={true}
                        />
                      )}
                    </FieldArray>
                  ) : null}
                </Modal.Body>

                <Modal.Footer>
                  <Button data-action="cancel" variant="link" onClick={doClose} id="remove-doc-cancel-btn">
                    {translate('CANCEL')}
                  </Button>
                  <Button
                    data-action="save"
                    className="btn btn-primary text-capitalize"
                    onClick={() => handleSubmit()}
                    id="remove-doc-submit-btn"
                  >
                    {translate('BROWSER_SEND_REQUEST')}
                  </Button>
                </Modal.Footer>
              </>
            )}
          </Formik>
        </Loader>
      </Modal>
    </>
  )
}

export default RemoveDocumentModal
